import React from 'react';
import { motion } from 'motion/react';
import { Keyboard, ClipboardPaste, Sparkles, CheckCircle, Lock } from 'lucide-react';
import { howItWorks } from '../data/mock';

const iconMap = {
  Keyboard,
  ClipboardPaste,
  Sparkles,
  CheckCircle
};

const HowItWorks = () => {
  const springConfig = {
    type: "spring",
    damping: 80,
    stiffness: 400,
    mass: 1
  };

  return (
    <section className="py-24 bg-gradient-to-br from-orange-50 to-pink-50">
      <div className="container">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={springConfig}
          className="text-center mb-16"
        >
          <h2 
            className="heading-large mb-4"
            style={{
              fontSize: 'clamp(32px, 4vw, 48px)',
              fontWeight: 600,
              color: '#0A0A0A',
              letterSpacing: '-0.01em'
            }}
          >
            How Typeit4u Works
          </h2>
          <p 
            className="body-large max-w-2xl mx-auto"
            style={{
              fontSize: '18px',
              color: '#3C3B3D',
              lineHeight: 1.6
            }}
          >
            Simple. Private. Powerful. You always stay in control.
          </p>
        </motion.div>

        {/* Steps Grid */}
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {howItWorks.map((item, index) => {
              const Icon = iconMap[item.icon];
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ ...springConfig, delay: index * 0.15 }}
                  className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100"
                >
                  <div className="flex items-start gap-4">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{
                        background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B9D 100%)'
                      }}
                    >
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <span 
                        className="text-xs font-semibold uppercase tracking-wide mb-2 block"
                        style={{ color: '#FF6B9D' }}
                      >
                        {item.step}
                      </span>
                      <h3 
                        className="font-semibold mb-2"
                        style={{ color: '#0A0A0A', fontSize: '18px' }}
                      >
                        {item.title}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Privacy Note */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ ...springConfig, delay: 0.6 }}
            className="mt-8 text-center"
          >
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm rounded-full px-6 py-3 border border-gray-200">
              <Lock className="w-4 h-4" style={{ color: '#FF6B9D' }} />
              <span className="text-sm text-gray-700">
                We never access your keyboard input automatically. You choose what AI sees.
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
